import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { MotionSection } from "../../components/Motion";
import { motion } from "framer-motion";
import { FileText, Calendar } from "lucide-react";
import { toAbsoluteAssetUrl } from "../../lib/api";
import { openStoredLibraryFile } from "../../lib/portalFiles";
import ProjectTimelineCard from "../../components/ProjectTimelineCard";
import WorkspaceScopeWorkboard from "../../components/homeTasks/WorkspaceHomeTasks";
import { toast } from "sonner";

const WELCOME_STORAGE_KEY = "taistu_portal_welcome_message";
const HOME_INTRO_SEEN_KEY = "taistu_portal_home_intro_seen";
const HOME_REVEAL_EASE = [0.22, 1, 0.36, 1];

function useHomeIntroReveal() {
  const [disableReveal] = useState(() => {
    try {
      return typeof window !== "undefined" && window.localStorage.getItem(HOME_INTRO_SEEN_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const id = window.setTimeout(() => {
      try {
        window.localStorage.setItem(HOME_INTRO_SEEN_KEY, "1");
      } catch {
        /* ignore */
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, []);

  return { disableReveal };
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const fn = () => setReduced(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return reduced;
}

function WelcomeHero({ client, user, disableMotionReveal }) {
  const defaultWelcome = React.useMemo(() => {
    const first = user?.name?.split(" ")?.[0] || "there";
    return `Welcome, ${first}.`;
  }, [user?.name]);

  const skipNextTypewriter = React.useRef(false);
  const [customStored, setCustomStored] = useState(() => {
    try {
      return typeof window !== "undefined" ? window.localStorage.getItem(WELCOME_STORAGE_KEY) : null;
    } catch {
      return null;
    }
  });
  const fullText = customStored?.trim() ? customStored.trim() : defaultWelcome;
  const [visibleLen, setVisibleLen] = useState(0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (editing) return;
    if (reducedMotion) {
      setVisibleLen(fullText.length);
      return;
    }
    if (skipNextTypewriter.current) {
      skipNextTypewriter.current = false;
      setVisibleLen(fullText.length);
      return;
    }
    setVisibleLen(0);
    const len = fullText.length;
    if (len === 0) return;
    const n = Math.max(len, 1);
    const stepMs = Math.max(26, Math.min(72, Math.floor(3400 / n)));
    const durationMs = stepMs * len;
    let rafId = 0;
    let startTs = null;
    let lastEmitted = -1;
    const tick = (ts) => {
      if (startTs === null) startTs = ts;
      const elapsed = ts - startTs;
      const u = Math.min(1, elapsed / durationMs);
      const next = u >= 1 ? len : Math.floor(u * len);
      if (next !== lastEmitted) {
        lastEmitted = next;
        setVisibleLen(next);
      }
      if (next < len) rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, [fullText, editing, reducedMotion]);

  const startEdit = useCallback(() => {
    setDraft(fullText);
    setEditing(true);
  }, [fullText]);

  const persistDraftOnBlur = useCallback(() => {
    const t = draft.trim();
    try {
      if (!t) {
        window.localStorage.removeItem(WELCOME_STORAGE_KEY);
        setCustomStored(null);
      } else {
        window.localStorage.setItem(WELCOME_STORAGE_KEY, t);
        setCustomStored(t);
      }
    } catch {
      /* ignore */
    }
    skipNextTypewriter.current = true;
    setEditing(false);
  }, [draft]);

  const welcomeHeadingType = "font-nohemi !font-extralight text-2xl tracking-[-0.015em]";
  const inputHeadingClass =
    `m-0 w-full max-w-full bg-transparent ${welcomeHeadingType} pr-[120px] sm:pr-[160px] md:pr-[140px] lg:pr-[200px] outline-none border-0 border-b border-transparent focus-visible:border-[var(--t-accent)] rounded-none min-h-[2.75rem] md:min-h-[3.25rem]`;

  return (
    <MotionSection
      className="py-6 sm:py-8 md:pt-10 md:pb-0 lg:pt-12"
      aria-labelledby={editing ? "home-welcome-edit" : "home-welcome-heading"}
      disableReveal={disableMotionReveal}
    >
      <div className="relative">
        {!editing ? (
          <h1
            id="home-welcome-heading"
            tabIndex={0}
            role="button"
            title="Click to edit"
            className={`m-0 cursor-text ${welcomeHeadingType} pr-[120px] sm:pr-[160px] md:pr-[140px] lg:pr-[200px] outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[var(--t-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--t-bg)] rounded-[2px]`}
            onClick={startEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                startEdit();
              }
            }}
            aria-label="Welcome message, click to edit"
          >
            {fullText.slice(0, visibleLen)}
            {!reducedMotion && visibleLen < fullText.length ? (
              <span
                className="inline-block w-[2px] h-[0.82em] bg-current ml-1 align-[-0.06em] motion-safe:animate-welcome-caret"
                aria-hidden
              />
            ) : null}
          </h1>
        ) : (
          <div className="max-w-full min-w-0">
            <label htmlFor="home-welcome-edit" className="sr-only">
              Welcome message
            </label>
            <input
              id="home-welcome-edit"
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={persistDraftOnBlur}
              className={inputHeadingClass}
              autoComplete="off"
              autoFocus
              data-testid="welcome-edit-input"
            />
          </div>
        )}
        {client?.company_logo_url && (
          <motion.div
            className="pointer-events-none absolute bottom-0 right-0 flex h-[52px] w-[120px] items-center justify-end sm:h-16 sm:w-[160px] md:h-[72px] md:w-[140px] lg:h-[88px] lg:w-[200px]"
            initial={disableMotionReveal || reducedMotion ? false : { opacity: 0, y: 22 }}
            animate={disableMotionReveal || reducedMotion ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.72, ease: HOME_REVEAL_EASE, delay: 0.16 }}
          >
            <img
              src={toAbsoluteAssetUrl(client.company_logo_url)}
              alt={`${client.name} logo`}
              className="max-h-full max-w-full rounded-[var(--t-event-pill-radius)] object-contain"
            />
          </motion.div>
        )}
      </div>
    </MotionSection>
  );
}

function formatMeetingWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatSharedWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function HomePage() {
  const { client, user } = useAuth();
  const [data, setData] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [selectedScopeId, setSelectedScopeId] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [taskBusy, setTaskBusy] = useState(false);
  const { disableReveal } = useHomeIntroReveal();
  const isAdmin = user?.role === "admin";

  const loadDashboard = useCallback(async () => {
    if (!client) return;
    const params = user?.role === "admin" ? `?client_id=${client.id}` : "";
    const { data: payload } = await api.get(`/portal/dashboard${params}`);
    setData(payload);
  }, [client, user]);

  useEffect(() => {
    if (!client) return;
    loadDashboard().then(() => setSelectedPeriod(null)).catch(() => {
      toast.error("Could not load home");
    });
  }, [client, loadDashboard]);

  const scopes = useMemo(() => {
    const steps = data?.scope_progress?.steps;
    const fallback = data?.workboard_scopes;
    const source =
      Array.isArray(steps) && steps.length > 0
        ? steps
        : Array.isArray(fallback)
          ? fallback
          : [];
    if (!source.length) return [];
    return source.map((step, idx) => ({
      id: String(step.milestone_id || step.key || `step-${idx}`),
      milestoneId: step.milestone_id || null,
      title: String(step.title || `Scope ${idx + 1}`),
      progressPct:
        step.progress_pct != null ? Math.min(100, Math.max(0, Number(step.progress_pct) || 0)) : null,
      tasks: (Array.isArray(step.tasks) ? step.tasks : [])
        .map((t, tIdx) => ({
          id: String(t.id || `${step.key || idx}-task-${tIdx}`),
          title: String(t.title || "").trim(),
          done: Boolean(t.done),
          due: t.due || null,
        }))
        .filter((t) => t.title),
    }));
  }, [data]);

  useEffect(() => {
    if (!scopes.length) {
      setSelectedScopeId(null);
      return;
    }
    setSelectedScopeId((prev) =>
      prev && scopes.some((s) => s.id === prev) ? prev : scopes[0].id,
    );
  }, [scopes]);

  const onSelectScope = useCallback((scopeId) => {
    setSelectedScopeId(scopeId);
  }, []);

  const onSaveTimeline = useCallback(
    async ({ timeline_start, timeline_end }) => {
      if (!client?.id) return;
      try {
        await api.patch(`/admin/clients/${client.id}/timeline-range`, {
          timeline_start: timeline_start || null,
          timeline_end: timeline_end || null,
        });
        await loadDashboard();
        setSelectedPeriod(null);
        toast.success("Timeline updated");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not update timeline");
        throw err;
      }
    },
    [client?.id, loadDashboard],
  );

  const patchScopeDeliverables = useCallback(
    async (scope, deliverables) => {
      if (!scope?.milestoneId || taskBusy) return;
      setTaskBusy(true);
      try {
        await api.patch(`/portal/milestones/${scope.milestoneId}`, { deliverables });
        await loadDashboard();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not update tasks");
      } finally {
        setTaskBusy(false);
      }
    },
    [loadDashboard, taskBusy],
  );

  const onToggleTask = useCallback(
    (taskId) => {
      const scope = scopes.find((s) => s.id === selectedScopeId) || scopes[0];
      if (!scope) return;
      const deliverables = scope.tasks.map((t) => ({
        id: t.id,
        label: t.title,
        done: t.id === taskId ? !t.done : Boolean(t.done),
        ...(t.due ? { due: t.due } : {}),
      }));
      patchScopeDeliverables(scope, deliverables);
    },
    [scopes, selectedScopeId, patchScopeDeliverables],
  );

  const onUpdateTask = useCallback(
    (taskId, patch = {}) => {
      const scope = scopes.find((s) => s.id === selectedScopeId) || scopes[0];
      if (!scope) return;
      const deliverables = scope.tasks.map((t) => {
        const isTarget = t.id === taskId;
        const title = isTarget && patch.title != null ? String(patch.title).trim() : t.title;
        const due =
          isTarget && patch.due != null ? String(patch.due).slice(0, 10) : t.due || null;
        return {
          id: t.id,
          label: title,
          done: Boolean(t.done),
          ...(due ? { due } : {}),
        };
      });
      patchScopeDeliverables(scope, deliverables);
    },
    [scopes, selectedScopeId, patchScopeDeliverables],
  );

  const onDeleteTask = useCallback(
    (taskId) => {
      const scope = scopes.find((s) => s.id === selectedScopeId) || scopes[0];
      if (!scope) return;
      const deliverables = scope.tasks
        .filter((t) => t.id !== taskId)
        .map((t) => ({
          id: t.id,
          label: t.title,
          done: Boolean(t.done),
          ...(t.due ? { due: t.due } : {}),
        }));
      patchScopeDeliverables(scope, deliverables);
    },
    [scopes, selectedScopeId, patchScopeDeliverables],
  );

  const onAddTask = useCallback(
    async (payload) => {
      const scope = scopes.find((s) => s.id === selectedScopeId) || scopes[0];
      if (!scope?.milestoneId) {
        toast.error("Scope milestone missing — reopen the client workspace");
        return;
      }
      const label = String(payload?.title || "").trim();
      if (!label) return;
      const deliverables = [
        ...scope.tasks.map((t) => ({
          id: t.id,
          label: t.title,
          done: Boolean(t.done),
          ...(t.due ? { due: t.due } : {}),
        })),
        {
          id: crypto.randomUUID(),
          label,
          done: false,
          ...(payload?.due ? { due: String(payload.due).slice(0, 10) } : {}),
        },
      ];
      await patchScopeDeliverables(scope, deliverables);
    },
    [scopes, selectedScopeId, patchScopeDeliverables],
  );

  const openSharedFile = useCallback(
    async (file) => {
      const id = (file?.file_id || file?.id || "").trim();
      if (!id || !client) return;
      setOpeningId(id);
      try {
        const ok = await openStoredLibraryFile(
          {
            id,
            name: file.file_name || file.name || "Shared file",
            file_type: file.file_type || "pdf",
            original_filename: file.original_filename || file.file_name || file.name,
            packet_kind: file.packet_kind || file.kind,
            note: file.note,
          },
          client,
          (fileId) => api.get(`/portal/files/item/${fileId}/download`, { responseType: "blob" }),
        );
        if (!ok) toast.error("Could not open file");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not open file");
      } finally {
        setOpeningId(null);
      }
    },
    [client],
  );

  if (!client) {
    return <div className="p-12 t-meta">No client context. Please sign in or select a client.</div>;
  }
  if (!data) return <div className="p-12 t-meta">Loading…</div>;

  const shared = Array.isArray(data.recent_shared_files) ? data.recent_shared_files : [];
  const nextMeeting = data.next_meeting || null;
  const scopeProgress = data.scope_progress || null;
  const currentStep = scopeProgress?.current_step || null;
  const upcomingStep = scopeProgress?.upcoming_step || null;
  const overallPct =
    scopeProgress?.overall_progress_pct != null
      ? scopeProgress.overall_progress_pct
      : scopeProgress?.progress_pct != null
        ? scopeProgress.progress_pct
        : data.progress_pct || 0;
  const timelinePeriods = Array.isArray(data.timeline_periods) ? data.timeline_periods : [];
  const showAdminTasks = isAdmin && scopes.length > 0;

  return (
    <div
      className={`min-h-0 min-w-0 max-w-full overflow-x-hidden p-4 sm:p-5 md:p-7${disableReveal ? "" : " t-fade-up"}`}
      data-testid="home-page"
    >
      {isAdmin ? (
        <header className="mb-6 sm:mb-8">
          <div className="label-eyebrow mb-2">Home</div>
          <h1 className="font-heading text-2xl tracking-tight !font-extralight md:text-3xl">
            {client.name}
          </h1>
        </header>
      ) : (
        <WelcomeHero client={client} user={user} disableMotionReveal={disableReveal} />
      )}

      <MotionSection className="mt-3 sm:mt-6 md:mt-8" disableReveal={disableReveal}>
        <ProjectTimelineCard
          client={data.client || client}
          activeCycle={data.active_cycle}
          periods={timelinePeriods}
          selectedPeriod={selectedPeriod}
          onSelectPeriod={setSelectedPeriod}
          canEdit={isAdmin}
          onSaveTimeline={isAdmin ? onSaveTimeline : undefined}
        />
      </MotionSection>

      <MotionSection
        className="mt-3 grid grid-cols-1 gap-3 sm:mt-6 md:mt-8 lg:grid-cols-2 lg:items-start"
        data-testid="project-health"
        disableReveal={disableReveal}
      >
        <div className="neo-panel p-5 md:p-6" data-testid="scope-progress-panel">
          <div className="label-eyebrow mb-3">Progress</div>
          <div className="mb-3 flex items-baseline gap-2">
            <span className="font-heading text-lg tracking-tight !font-light text-[var(--t-text)]">
              {overallPct}%
            </span>
            <span className="t-meta">complete</span>
          </div>
          <div className="neo-well relative h-2 overflow-hidden rounded-full">
            <motion.div
              className="absolute inset-y-0 left-0 rounded-full bg-[var(--t-accent)]"
              initial={disableReveal ? false : { width: 0 }}
              animate={{ width: `${overallPct || 0}%` }}
              transition={{ duration: 0.9, ease: HOME_REVEAL_EASE, delay: 0.12 }}
            />
          </div>
          {currentStep ? (
            <div className="mt-5 space-y-3" data-testid="scope-progress-current">
              <div>
                <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--t-text)]">
                  {currentStep.title}
                </div>
                <div className="t-meta mt-1 text-xs">
                  {Math.min(100, Math.max(0, Number(currentStep.progress_pct) || 0))}% of this item
                </div>
              </div>
              {upcomingStep ? (
                <div className="pt-1" data-testid="scope-progress-upcoming">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--t-accent)]">
                    Upcoming
                  </div>
                  <div className="mt-1 text-xs leading-snug text-[var(--t-accent)]">
                    {upcomingStep.title}
                  </div>
                  {upcomingStep.description ? (
                    <div className="mt-0.5 text-[11px] leading-snug text-[var(--t-accent)]/80 line-clamp-2">
                      {upcomingStep.description}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="neo-panel p-5 md:p-6" data-testid="next-meeting-panel">
          <div className="label-eyebrow mb-3">Next meeting</div>
          {nextMeeting ? (
            <div>
              <div className="mb-2 flex items-start gap-2">
                <Calendar size={16} strokeWidth={1.4} className="mt-0.5 shrink-0 text-[var(--t-accent)]" />
                <div className="font-heading text-lg tracking-tight !font-light leading-snug">
                  {nextMeeting.title}
                </div>
              </div>
              <div className="t-meta pl-6">{formatMeetingWhen(nextMeeting.start)}</div>
              {nextMeeting.location ? (
                <div className="t-meta mt-1 pl-6">{nextMeeting.location}</div>
              ) : null}
            </div>
          ) : (
            <div className="t-meta">No upcoming meeting scheduled.</div>
          )}
        </div>
      </MotionSection>

      {showAdminTasks ? (
        <MotionSection className="mt-3 sm:mt-6 md:mt-8" disableReveal={disableReveal}>
          <WorkspaceScopeWorkboard
            scopes={scopes}
            selectedScopeId={selectedScopeId}
            onSelectScope={onSelectScope}
            onToggle={onToggleTask}
            onAdd={onAddTask}
            onUpdate={onUpdateTask}
            onDelete={onDeleteTask}
          />
        </MotionSection>
      ) : null}

      <MotionSection className="mt-8 lg:mt-10" data-testid="recent-shared-files-panel" disableReveal={disableReveal}>
        <div className="mb-4">
          <h2 className="font-nohemi text-xl tracking-[-0.015em] !font-extralight">Shared with you</h2>
        </div>
        {shared.length === 0 ? (
          <div className="neo-panel p-6 t-meta">No files shared yet.</div>
        ) : (
          <div className="neo-panel divide-y divide-black/5 overflow-hidden">
            {shared.map((file, index) => {
              const id = file.file_id || file.id;
              const busy = openingId === id;
              return (
                <button
                  key={id || `${file.file_name}-${index}`}
                  type="button"
                  onClick={() => openSharedFile(file)}
                  disabled={busy}
                  className="flex w-full items-center gap-3 px-5 py-4 text-left t-row-hover disabled:opacity-60"
                  data-testid={`shared-file-${id}`}
                >
                  <FileText size={18} strokeWidth={1.4} className="shrink-0 text-[var(--t-accent)]" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-nohemi text-sm tracking-[-0.015em] !font-light">
                      {file.file_name || file.name || "Shared file"}
                    </div>
                    <div className="t-meta mt-0.5">
                      {[file.kind, formatSharedWhen(file.sent_at)].filter(Boolean).join(" · ") || "Shared file"}
                    </div>
                  </div>
                  <span className="t-meta shrink-0 text-xs uppercase tracking-[0.12em]">
                    {busy ? "Opening…" : "Open"}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </MotionSection>
    </div>
  );
}
